import { LoadingState, QueryError } from '#/components/common/loading-state'
import { toast } from 'sonner'
import { Button } from '#/components/ui/button'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Briefcase, Code2, FileText, ChevronRight, History, LayoutGrid, ScanSearch } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Wordmark } from '#/components/layout/wordmark'
import { Mascot } from '#/components/layout/mascot'
import { ChoiceCard } from '#/components/plan/choice-card'
import { PlanDrawer } from '#/components/plan/plan-drawer'
import { PlanLink } from '#/components/common/plan-link'
import { useWorkspaces } from '#/lib/api'
import { usePlansWhen } from '#/lib/api-ws'
import { useInboxWithWorkspaces } from '#/lib/api-inbox'
import { AttentionPill } from '#/components/inbox/attention-pill'
import { PrimaryAction } from '#/components/common/primary-action'
import { setBuilderMode, useBuilderMode } from '#/lib/simple-mode'
import { useNavigate } from '@tanstack/react-router'
import { Sparkles } from 'lucide-react'
import { useHydratedSession } from '#/lib/auth-client'
import { fmtDate } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/home')({ component: Home })

function Home() {
  return (
    <>
      <AppHeader />
      <main className="flex flex-1 flex-col overflow-y-auto">
        <section className="border-b bg-card/50" data-testid="brand-home-welcome">
          <div className="mx-auto flex w-full max-w-5xl items-center gap-5 px-6 py-8 sm:gap-8 sm:py-12">
            <Mascot size={144} eager className="size-20 sm:size-36" />
            <div className="min-w-0 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">{m.brand_home_eyebrow()}</p>
              <h1 className="text-2xl font-bold leading-tight sm:text-4xl">{m.brand_home_title()}</h1>
              <p className="max-w-xl text-sm text-muted-foreground sm:text-base">{m.brand_home_description()}</p>
            </div>
          </div>
        </section>
        <div className="mx-auto w-full max-w-5xl space-y-8 px-6 py-10">
          <SimpleBanner />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <ChoiceCard
              to="/plan"
              icon={<FileText className="size-5" />}
              tone="green"
              badge={m.landing_card_plan_badge()}
              title={m.landing_card_plan_title()}
              description={m.landing_card_plan_desc()}
            />
            <ChoiceCard
              to="/plan"
              search={{ flow: 'website' }}
              icon={<ScanSearch className="size-5" />}
              tone="blue"
              badge={m.site_home_badge()}
              title={m.site_choose_title()}
              description={m.site_choose_desc()}
              testId="home-card-website"
            />
            <ChoiceCard
              to="/plan"
              search={{ flow: 'existing' }}
              icon={<Code2 className="size-5" />}
              tone="amber"
              title={m.landing_card_codebase_title()}
              description={m.landing_card_codebase_desc()}
            />
            <ChoiceCard
              to="/room"
              icon={<LayoutGrid className="size-5" />}
              tone="violet"
              badge={m.dr_home_card_badge()}
              title={m.dr_home_card_title()}
              description={m.dr_home_card_desc()}
            />
            <ChoiceCard
              to="/consulting"
              icon={<Briefcase className="size-5" />}
              tone="blue"
              title={m.landing_card_consulting_title()}
              description={m.landing_card_consulting_desc()}
            />
          </div>
          <ContinueRow />
          <section className="rounded-2xl border bg-card/60 p-5">
            <h2 className="mb-4 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{m.landing_how_title()}</h2>
            <ol className="grid gap-4 sm:grid-cols-3">
              {[
                [m.landing_how_1_title(), m.landing_how_1_desc()],
                [m.landing_how_2_title(), m.landing_how_2_desc()],
                [m.landing_how_3_title(), m.landing_how_3_desc()],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-3">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary font-heading text-sm font-bold text-primary-foreground">{i + 1}</span>
                  <div>
                    <div className="font-heading font-semibold">{t}</div>
                    <p className="text-sm text-muted-foreground">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </main>
      <footer className="flex min-h-10 shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 border-t px-3 py-2 text-xs text-muted-foreground">
        <Link to="/docs" className="hover:text-foreground">{m.landing_footer_docs()}</Link>
        <Link to="/pricing" search={{ voucher: undefined }} className="hover:text-foreground">{m.landing_footer_pricing()}</Link>
        <Link to="/support" className="hover:text-foreground">{m.landing_footer_support()}</Link>
        <span>·</span>
        <Wordmark size="sm" />
      </footer>
    </>
  )
}

/** Non-tech track entry (Phase 0): one strip above the cards — see how it works, or switch this account to simple mode. */
function SimpleBanner() {
  const { data: session } = useHydratedSession()
  const { mode, isPending } = useBuilderMode()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  if (!session?.user || isPending) return null
  const simple = mode === 'simple'
  const toggle = async () => {
    setBusy(true)
    try {
      await setBuilderMode(simple ? 'dev' : 'simple')
      if (!simple) navigate({ to: '/plan', search: { flow: 'new' } })
    } catch {
      toast.error(m.common_error_generic())
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="home-simple-banner">
      <div className="flex min-w-0 items-start gap-3">
        <Sparkles className="mt-0.5 size-5 shrink-0 text-primary" />
        <div className="min-w-0">
          <p className="font-semibold">{simple ? m.simple_home_banner_on_title() : m.simple_home_banner_title()}</p>
          <p className="text-sm text-muted-foreground">{simple ? m.simple_home_banner_on_desc() : m.simple_home_banner_desc()}</p>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Link to="/mulai" className="rounded-lg border bg-card px-3 py-2 text-sm font-medium hover:bg-accent">{m.simple_home_banner_how()}</Link>
        <Button onClick={toggle} loading={busy} className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50" data-testid="home-simple-toggle">
          {simple ? m.simple_home_banner_turn_off() : m.simple_home_banner_turn_on()}
        </Button>
      </div>
    </section>
  )
}

/** "Pick up where you left off": last 3 plans of a signed-in user, straight under the four cards. */
function ContinueRow() {
  const { data: session } = useHydratedSession()
  const { data, isLoading, isError, isFetching, refetch } = usePlansWhen(Boolean(session?.user))
  const workspacesQuery = useWorkspaces(Boolean(session?.user))
  // Inspirasi F1/F2: one state pill per project + the button whose label follows that state
  const { data: attention } = useInboxWithWorkspaces(Boolean(session?.user))
  const { mode } = useBuilderMode()
  const [drawer, setDrawer] = useState(false)
  if (!session?.user) return null
  if (isLoading || workspacesQuery.isLoading) return <LoadingState compact label={m.loading_projects()} layout="cards" />
  if (isError && !data) return <QueryError onRetry={refetch} retrying={isFetching} />
  if (workspacesQuery.isError && !workspacesQuery.data) return <QueryError onRetry={workspacesQuery.refetch} retrying={workspacesQuery.isFetching} />
  const wsState = (id: string) => attention?.workspaces.find((w) => w.workspaceId === id)
  const waiting = attention?.counts.blocked ?? 0
  const allPlans = data?.plans ?? []
  const entries = [
    ...allPlans.map(plan => ({ kind: 'plan' as const, plan, updatedAt: plan.updatedAt })),
    ...(workspacesQuery.data?.workspaces ?? []).filter(w => !allPlans.some(p => p.workspaceId === w.id)).map(workspace => ({ kind: 'workspace' as const, workspace, updatedAt: workspace.updatedAt })),
  ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 3)
  if (!entries.length) return null
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><History className="size-3.5" /> {m.ws_continue_title()}</h2>
        <div className="flex items-center gap-3">
          {waiting > 0 ? <Link to="/inbox" className="text-xs font-medium text-status-progress-text hover:underline" data-testid="home-inbox-link">{m.inbox_count_blocked({ n: waiting })}</Link> : null}
          <button className="text-xs text-primary hover:underline" onClick={() => setDrawer(true)}>{m.ws_continue_all()}</button>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-3">
        {entries.map((entry) => {
          if (entry.kind === 'workspace') {
            const w = entry.workspace
            return (
              <Link key={w.id} to="/workspace/$workspaceId/$view" params={{ workspaceId: w.id, view: 'wiki' }} search={{}} className="flex min-w-0 items-center gap-3 rounded-xl border bg-card px-3 py-2.5 text-sm hover:border-primary" data-testid="home-workspace-card">
                <Code2 className="size-5 shrink-0 text-primary" />
                <span className="min-w-0 flex-1"><span className="block truncate font-medium">{w.name}</span><span className="block text-[11px] text-muted-foreground">{w.hasWiki ? m.existing_saved() : w.codebaseStatus === 'none' ? m.existing_waiting() : m.existing_analysing()} · {fmtDate(w.updatedAt)}</span></span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </Link>
            )
          }
          const p = entry.plan
          const a = wsState(p.workspaceId)
          return (
            <div key={p.id} className="flex min-w-0 flex-col gap-2 rounded-xl border bg-card px-3 py-2.5 text-sm" data-testid="home-plan-card">
              <PlanLink plan={p} className="group flex min-w-0 items-center gap-3 hover:text-primary">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground"><FileText className="size-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{p.title}</span>
                  <span className="block truncate text-[11px] text-muted-foreground">
                    {p.status === 'generating' ? <span className="text-status-progress-text">{m.ws_plan_generating()}</span> : p.status !== 'completed' ? m.ws_plan_draft() : p.workspaceName} · {fmtDate(p.updatedAt)}
                  </span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground group-hover:text-primary" />
              </PlanLink>
              <div className="flex flex-wrap items-center justify-between gap-2">
                {a ? <AttentionPill state={p.status === 'generating' ? 'working' : a.state} /> : <span />}
                <PrimaryAction planId={p.id} mode={mode} size="sm" />
              </div>
              {a && a.freshDone > 0 && a.state === 'done' ? <span className="text-[11px] text-status-done-text">{m.inbox_ws_fresh_done({ n: a.freshDone })}</span> : null}
            </div>
          )
        })}
      </div>
      <PlanDrawer open={drawer} onOpenChange={setDrawer} />
    </section>
  )
}
